import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ErrorHandlerService } from '@app/_services/errorHandler.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AcceptTermsDialogResult } from './accept-terms-dialog.result';

@Component({
  selector: 'app-accept-terms-dialog',
  templateUrl: './accept-terms-dialog.component.html',
  styleUrls: ['./accept-terms-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatProgressSpinner, FormsModule, ReactiveFormsModule, AsyncPipe, TranslatePipe],
})
export class AcceptTermsDialogComponent implements OnInit, AfterViewInit {
  private readonly httpClient = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly modalRef = inject(BsModalRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  title!: string;
  text!: string;
  button_confirm_text!: string;
  button_cancel_text!: string;
  acceptTermsUrl!: string;

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  htmlSrc?: SafeHtml;
  form!: UntypedFormGroup;
  onClose!: Subject<AcceptTermsDialogResult>;

  ngOnInit(): void {
    this.loadingSubject.next(true);
    this.onClose = new Subject();

    this.form = new UntypedFormGroup({
      acceptTerms: new UntypedFormControl(false, [Validators.requiredTrue]),
    });
  }

  ngAfterViewInit(): void {
    this.httpClient
      .get(this.acceptTermsUrl, { responseType: 'text' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.htmlSrc = this.sanitizer.bypassSecurityTrustHtml(result);
          this.loadingSubject.next(false);
        },
        error: (errorResponse: HttpErrorResponse) => {
          console.error(errorResponse);
          this.loadingSubject.next(false);

          this.errorHandler.displayErrorDialog(errorResponse)?.then(() => {
            this.onCancel();
          });
        },
      });
  }

  get f() {
    return this.form.controls;
  }

  onConfirm(): void {
    this.onClose.next({ accepted: true });
    this.modalRef.hide();
  }

  onCancel(): void {
    this.onClose.next({ accepted: false });
    this.modalRef.hide();
  }

  static showDialogTranslated(
    translate: TranslateService,
    modalService: BsModalService,
    acceptTermsUrl: string,
    title?: string,
    text?: string,
    ok?: string,
    cancel?: string,
    modalClass?: string,
    translateParams?: object,
  ): Promise<BsModalRef<AcceptTermsDialogComponent>> {
    return new Promise<BsModalRef<AcceptTermsDialogComponent>>((resolve) => {
      const resourceArray = [];

      if (title) {
        resourceArray.push(title);
      }

      if (text) {
        resourceArray.push(text);
      }

      if (ok) {
        resourceArray.push(ok);
      }

      if (cancel) {
        resourceArray.push(cancel);
      }

      firstValueFrom(translate.get(resourceArray, translateParams)).then((res) => {
        const titleRes = title ? res[title] : '';
        const textRes = text ? res[text] : '';
        const okRes = ok ? res[ok] : undefined;
        const cancelRes = cancel ? res[cancel] : undefined;

        const modalRef = modalService.show(AcceptTermsDialogComponent, {
          class: modalClass,
          ignoreBackdropClick: true,
          initialState: {
            title: titleRes,
            text: textRes,
            button_confirm_text: okRes,
            button_cancel_text: cancelRes,
            acceptTermsUrl: acceptTermsUrl,
          },
        });
        resolve(modalRef);
      });
    });
  }
}
